import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { BACKEND_URL } from '../runtimeConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Code2, Copy } from 'lucide-react';

export const CONSENT_EMBED_HINT =
  'Paste this line into the <head> of every landing page of the campaign. The banner shows only while it is enabled above, and only to visitors from the chosen regions.';

// The one line an operator pastes into a landing page to show the banner.
export default function ConsentEmbed() {
  const { t } = useT();
  const [campaigns, setCampaigns] = useState([]);
  const [trackingId, setTrackingId] = useState('');

  useEffect(() => {
    axios
      .get(API + '/campaigns')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data && r.data.items) || [];
        setCampaigns(list);
        if (list[0] && list[0].tracking_id) setTrackingId(list[0].tracking_id);
      })
      .catch(() => {});
  }, []);

  const base = String(BACKEND_URL || window.location.origin).replace(/\/$/, '');
  const snippet =
    '<script src="' + base + '/api/public/consent/consent.js" data-tracking-id="' +
    (trackingId || 'TRACKING_ID') + '" async></script>';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success(t('Copied'));
    } catch (_e) {
      toast.error(t('Action failed'));
    }
  };

  return (
    <Card className="mb-6" data-testid="consent-embed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Code2 className="w-4 h-4" /> {t('Add the banner to your landing page')}
        </CardTitle>
        <CardDescription>{t(CONSENT_EMBED_HINT)}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('Campaign')}</span>
          <select
            className="h-9 max-w-md rounded-md border border-border bg-background px-2 text-sm"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            data-testid="consent-embed-campaign"
          >
            {campaigns.length === 0 && <option value="">{t('No campaigns yet')}</option>}
            {campaigns.map((c) => (
              <option key={c.id} value={c.tracking_id || ''}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">{snippet}</pre>
        <Button size="sm" variant="outline" onClick={copy}>
          <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('Copy code')}
        </Button>
      </CardContent>
    </Card>
  );
}
